import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/domains/internal/auth/services/authService';
import { isTenantDomain } from '@/utils/tenantDomain';

/**
 * Component that redirects users to their appropriate default page
 * based on user type and permissions
 */
const DefaultRedirect: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const redirectToDefault = async () => {
      // Check if we're on the public domain (no tenant subdomain)
      // If so, redirect to admin portal login
      if (!isTenantDomain()) {
        navigate('/admin/login', { replace: true });
        return;
      }

      // We're on a tenant subdomain - check for tenant staff authentication
      const user = authService.getUser();

      if (!user) {
        // Not logged in - go to tenant login
        navigate('/login', { replace: true });
        return;
      }

      // Handle referral portal users (referral sources and team members)
      if (user.user_type === 'referral' || user.user_type === 'referral_team') {
        // For team members, check permissions
        if (user.user_type === 'referral_team') {
          // If they can view commission, go to dashboard
          if (authService.hasPermission('can_view_commission')) {
            navigate('/referral-portal/dashboard', { replace: true });
          } else {
            // Otherwise go to lab orders
            navigate('/referral-portal/lab-orders', { replace: true });
          }
        } else {
          // Referral sources go to dashboard
          navigate('/referral-portal/dashboard', { replace: true });
        }
        return;
      }

      // Handle staff users - go to main dashboard
      navigate('/dashboard', { replace: true });
    };

    redirectToDefault();
  }, [navigate]);

  // Show loading while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>
  );
};

export default DefaultRedirect;
